import { AxiosCalls } from './Functions/AxiosCalls';
import { handleDefaultApiErrors } from 'Utils/Global/apiErrorHandling';
import type {
  AddOrderRequestDTO,
  ForbiddenAPIHandlerParams,
  IHandleAddOrderParams,
  OrderItemSummary,
} from '@model/Order.model';

import type { IPaymentMethodView } from '@model/PaymentMethod.model';
import type { IDeliveryAddressView, IDeliveryCarrier } from '@model/DeliveryAddress.model';
import {
  IHandleGetOrderDetailsParams,
  IHandleGetOrdersHistoryParams,
  IOrderCardProps,
  ORDER_STATUS_OPTIONS,
  OrderDetails,
  OrderStatus,
  OrderStatusOption,
  OrdersHistoryResponse,
} from '../Model/OrdersHistory.model';

export class OrderService {
  computeSubtotal(items: OrderItemSummary[]): number {
    return (items ?? []).reduce((sum: number, it: OrderItemSummary) => {
      return sum + Number(it.totalPrice || 0);
    }, 0);
  }

  computeGrandTotal(params: { items: OrderItemSummary[]; deliveryFee: number }): {
    subtotal: number;
    grandTotal: number;
  } {
    const { items, deliveryFee } = params;

    const subtotal = this.computeSubtotal(items);
    const fee = Number(deliveryFee || 0);

    return {
      subtotal,
      grandTotal: subtotal + fee,
    };
  }

  getItemsCount(items: OrderItemSummary[]): number {
    return (items ?? []).reduce((sum, it) => sum + Number(it.quantity || 0), 0);
  }

  canPlaceOrder(params: {
    disableCheckout: boolean;
    itemsCount: number;
    hasPayment: boolean;
    hasAddress: boolean;
    hasCarrier: boolean;
  }): boolean {
    const { disableCheckout, itemsCount, hasPayment, hasAddress, hasCarrier } = params;

    if (disableCheckout) return false;
    if (itemsCount <= 0) return false;
    if (!hasPayment) return false;
    if (!hasAddress) return false;
    if (!hasCarrier) return false;

    return true;
  }

  buildAddOrderPayload(params: {
    items: OrderItemSummary[];
    selectedPaymentMethod: IPaymentMethodView | null;
    selectedDeliveryAddress: IDeliveryAddressView | null;
    selectedCarrier: IDeliveryCarrier | null;
  }): AddOrderRequestDTO {
    const { items, selectedPaymentMethod, selectedDeliveryAddress, selectedCarrier } = params;

    return {
      paymentMethodId: selectedPaymentMethod?.id ?? '',
      deliveryAddressId: selectedDeliveryAddress?.id ?? '',
      deliveryCarrierId: selectedCarrier?.id ?? '',
      deliveryCost: Number(selectedCarrier?.fees ?? 0),
      items: (items ?? []).map((it) => ({
        itemId: it.id,
        quantity: Number(it.quantity || 0),
        unitPrice: Number(it.unitPrice || 0),
      })),
    };
  }

  buildConfirmMessage(params: { itemsCount: number; grandTotal: number }): string {
    const { itemsCount, grandTotal } = params;
    const itemWord = itemsCount === 1 ? 'item' : 'items';
    return `You're about to place an order for ${itemsCount} ${itemWord}.\n\nTotal to pay: $${grandTotal.toFixed(
      2
    )}\n\nDo you want to confirm?`;
  }

  async handleAddOrder({
    token,
    items,
    selectedPaymentMethod,
    selectedDeliveryAddress,
    selectedCarrier,
    setLoading,
    setApiError,
    onAuthError,
    showPopup,
    setConfirmOpen,
    navigate,
    removeOrderedItemsFromCart,
    removeManyFromCart
  }: IHandleAddOrderParams): Promise<void> {
    setLoading(true);
    setApiError(null);

    try {
      const payload = this.buildAddOrderPayload({
        items,
        selectedPaymentMethod,
        selectedDeliveryAddress,
        selectedCarrier,
      });

      await AxiosCalls.Post<void, AddOrderRequestDTO>('/orders/add-order', payload, token);

      setConfirmOpen(false);
      await removeOrderedItemsFromCart({
            token: token,
            orderItems: items,
            onAuthError,
            removeManyFromCart,
          });
      navigate('/order-success', { replace: true });
    } catch (err) {
      const onForbidden = this.handleForbiddenAPIResponse({
        showPopup,
        navigate,
      });

      const msg = handleDefaultApiErrors(err, {
        hadToken: Boolean(token),
        onAuthError,
        onForbidden,
      });

      setApiError(msg);
    } finally {
      setLoading(false);
    }
  }

  handleForbiddenAPIResponse({showPopup, navigate}:ForbiddenAPIHandlerParams){
    return (msg: string) => {
      showPopup(msg);
      navigate('/', { replace: true });
    };
  }

  handleOpenConfirm({
    disableCheckout,
    setApiError,
    setConfirmOpen,
  }: {
    disableCheckout: boolean;
    setApiError: (v: string | null) => void;
    setConfirmOpen: (v: boolean) => void;
  }) {
    if (disableCheckout) return;

    setApiError(null);
    setConfirmOpen(true);
  }

  // TODO: Remove unnecessary mapping
  // mapOrdersHistoryResponseToCards(res: AxiosResponse<OrdersHistoryResponse>): IOrderCardProps[] {
  //   const data = res?.data ?? res;
  //   const orders = data?.data?.orders ?? [];

  //   if (!Array.isArray(orders)) return [];

  //   return orders.map((o: UserOrderListItem) => ({
  //     id: String(o.id),
  //     orderNumber: String(o.orderNumber ?? ''),
  //     itemsCount: Number(o.itemsCount ?? 0),
  //     totalToBePaid: Number(o.totalToBePaid ?? 0),
  //     createdAt: String(o.createdAt ?? ''),
  //     status: o.status as OrderStatus,
  //   }));
  // }

  async handleGetOrdersHistory({
    token,
    setLoading,
    setApiError,
    onAuthError,
    showPopup,
    navigate,
    setOrders,
  }: IHandleGetOrdersHistoryParams): Promise<void> {
    const cleanToken = (token ?? '').trim();
    if (!cleanToken) return;

    setLoading(true);
    setApiError(null);

    try {
      const res = await AxiosCalls.Get<OrdersHistoryResponse>('/orders/orders-history', cleanToken);

      const data = res?.data ?? res;
      const orders = data?.data?.orders ?? [];
      // const mappedOrders = this.mapOrdersHistoryResponseToCards(res);

      setOrders(orders);
    } catch (err) {
      const onForbidden = this.handleForbiddenAPIResponse({
        showPopup,
        navigate,
      });

      const msg = handleDefaultApiErrors(err, {
        hadToken: Boolean(cleanToken),
        onAuthError,
        onForbidden,
      });

      setApiError(msg);
    } finally {
      setLoading(false);
    }
  }

  getSelectedStatusOptions(value: OrderStatus[]): OrderStatusOption[] {
    return ORDER_STATUS_OPTIONS.filter((o) => (value ?? []).includes(o.value));
  }

  filterOrdersByStatus(orders: IOrderCardProps[], statusFilter: OrderStatus[]): IOrderCardProps[] {
    const list = Array.isArray(orders) ? orders : [];

    if (!statusFilter || statusFilter.length === 0) return list;

    const set = new Set(statusFilter);
    return list.filter((o) => set.has(o.status));
  }

  computeOrderItemsSubtotal(order: OrderDetails | null): number {
    const list = order?.items ?? [];
    return list.reduce((sum, it) => sum + Number(it.totalPrice || 0), 0);
  }

  // TODO: Remove unnecessary mapping
  // mapOrderDetailsResponse(res: AxiosResponse<OrderDetails>): OrderDetails {
  //   const orderPayload = (res?.data ?? res);

  //   return {
  //     id: String(orderPayload.id),
  //     orderNumber: String(orderPayload.orderNumber ?? ''),
  //     status: orderPayload.status as OrderStatus,
  //     createdAt: String(orderPayload.createdAt ?? ''),
  //     lastModifiedAt: String(orderPayload.lastModifiedAt ?? ''),
  //     totalToBePaid: Number(orderPayload.totalToBePaid ?? 0),
  //     deliveryCost: Number(orderPayload.deliveryCost ?? 0),

  //     paymentMethod: {
  //       id: String(orderPayload.paymentMethod.id),
  //       name: String(orderPayload.paymentMethod.name),
  //     },

  //     deliveryCarrier: {
  //       id: String(orderPayload.deliveryCarrier.id),
  //       name: String(orderPayload.deliveryCarrier.name),
  //       icon: String(orderPayload.deliveryCarrier.icon),
  //       deliveryTimeEstimation: String(orderPayload.deliveryCarrier.deliveryTimeEstimation),
  //       fees: Number(orderPayload.deliveryCarrier.fees ?? 0),
  //     },

  //     deliveryAddress: {
  //       id: String(orderPayload.deliveryAddress.id),
  //       addressLabel: String(orderPayload.deliveryAddress.addressLabel),
  //       street: String(orderPayload.deliveryAddress.street),
  //       houseNumber: String(orderPayload.deliveryAddress.houseNumber),
  //       city: String(orderPayload.deliveryAddress.city),
  //       region: String(orderPayload.deliveryAddress.region),
  //       country: String(orderPayload.deliveryAddress.country),
  //       postalCode: orderPayload.deliveryAddress.postalCode
  //         ? String(orderPayload.deliveryAddress.postalCode)
  //         : null,
  //       additionalInformation: orderPayload.deliveryAddress.additionalInformation
  //         ? String(orderPayload.deliveryAddress.additionalInformation)
  //         : null,
  //       isDefaultAddress: Boolean(orderPayload.deliveryAddress.isDefaultAddress),
  //     },

  //     items: Array.isArray(orderPayload.items)
  //       ? orderPayload.items.map((item: OrderDetailsItem) => ({
  //           id: String(item.id ?? ''),
  //           itemId: String(item.itemId ?? ''),
  //           quantity: Number(item.quantity ?? 0),
  //           unitPrice: Number(item.unitPrice ?? 0),
  //           totalPrice: Number(item.totalPrice ?? 0),
  //           name: String(item.name ?? ''),
  //           imageUrl: item.imageUrl ? String(item.imageUrl) : null,
  //         }))
  //       : [],
  //   };
  // }

  async handleGetOrderDetails({
    token,
    orderId,
    setLoading,
    setApiError,
    onAuthError,
    onForbidden,
    setOrder,
  }: IHandleGetOrderDetailsParams): Promise<void> {
    const cleanToken = (token ?? '').trim();
    const cleanOrderId = String(orderId ?? '').trim();
    if (!cleanToken || !cleanOrderId) return;

    setLoading(true);
    setApiError(null);

    try {
      const res = await AxiosCalls.Get<OrderDetails>(`/orders/getone/${cleanOrderId}`, cleanToken);
      const mapped = res?.data ?? res;
      setOrder(mapped);
    } catch (err) {
      const msg = handleDefaultApiErrors(err, {
        hadToken: Boolean(cleanToken),
        onAuthError,
        onForbidden,
      });
      setApiError(msg);
    } finally {
      setLoading(false);
    }
  }
}
